import type { CSSProperties, Dispatch } from "react"

import type { SegmentAnalysisAction } from "../hooks/use-segment-analysis"
import type { Segment, SegmentChartKind } from "../types"
import type { TreemapGroup } from "../utils/treemap-layout"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { marketColors } from "../data/mock-segment-analysis-data"
import { COMPANY_NAME } from "../utils/branding"
import { readableTextOn, svgHaloProps } from "../utils/readable-text"
import { fitAxisDomain, formatAxisTick } from "../utils/segment-analysis-chart-utils"
import { consolidatedTrend, segmentAddressableTotal } from "../utils/segment-analysis-utils"
import { layoutTreemap, truncateTileLabel } from "../utils/treemap-layout"

type SegmentAnalysisChartsProps = {
  /** Which graph to render — the user picks one in Customize (§G9). */
  chart: SegmentChartKind
  dispatch: Dispatch<SegmentAnalysisAction>
  segments: Segment[]
  selectedSegmentId: string | null
  showTotal: boolean
}

const chartPanelClass =
  // Single-chart layout (§G9): keep the panel full-width (matches the KPI row + cards);
  // only cap the chart HEIGHT so a full-width SVG (h-auto keeps its aspect ratio) can't
  // blow the panel up to ~1200px tall. The chart's viewBox + preserveAspectRatio centers it.
  "card-interactive rounded-xl border border-border bg-card p-4 shadow-sm [&_svg]:mx-auto [&_svg]:max-h-[460px]"

export function SegmentAnalysisCharts({
  chart,
  dispatch,
  segments,
  selectedSegmentId,
  showTotal,
}: SegmentAnalysisChartsProps) {
  if (chart === "scores") {
    return (
      <article className={chartPanelClass}>
        <ChartHeader
          subtitle="Each dot = one segment (1-10 scale)"
          title="Attractiveness x Strategic fit"
        />
        <ScoreMatrixChart
          dispatch={dispatch}
          segments={segments}
          selectedSegmentId={selectedSegmentId}
        />
      </article>
    )
  }
  if (chart === "treemap") {
    return (
      <article className={chartPanelClass}>
        <ChartHeader subtitle="Treemap, MEUR" title="Sales by market group" />
        <TreemapChart segments={segments} />
      </article>
    )
  }
  if (chart === "addressable") {
    return (
      <article className={chartPanelClass}>
        <ChartHeader
          subtitle="Treemap by addressable opportunity (MEUR)"
          title="Addressable opportunity by market group"
        />
        <TreemapChart segments={segments} sizeOf={segmentAddressableTotal} />
      </article>
    )
  }
  if (chart === "trend") {
    return (
      <article className={chartPanelClass}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <ChartHeader
            subtitle={`${segments.length} segment${segments.length === 1 ? "" : "s"} - last 12 months`}
            title={`Consolidated ${COMPANY_NAME} MEUR`}
          />
          <Button
            variant={showTotal ? "default" : "ghost"}
            className={cn(
              "h-auto rounded-md px-3 py-1 text-xs font-semibold",
              !showTotal &&
                "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
            onClick={() => dispatch({ type: "toggleTotalLine" })}
          >
            Total {showTotal ? "ON" : "OFF"}
          </Button>
        </div>
        <TrendChart segments={segments} showTotal={showTotal} />
      </article>
    )
  }
  // Default: bubble (Market Potential — addressable market MEUR x growth x sales).
  return (
    <article className={chartPanelClass}>
      {/* Axis description as a smaller secondary label (#3306 #28) — the
          regular ChartHeader subtitle reads as a caption; this one steps
          down further since it's explanatory, not the chart's subject. Shares
          ChartHeader instead of forking its markup (review finding #3306 #7-3);
          `text-[11px]` (below the 12px caption level, above the 10px slot
          reserved for the version number) and the plain `text-muted-foreground`
          token (no opacity-derived colour, which fell below the design
          system's 4.9:1 minimum) keep it on the documented scale. */}
      <ChartHeader
        subtitle={`Addressable market (MEUR) x growth x ${COMPANY_NAME} sales (bubble size)`}
        subtitleClassName="text-[11px]"
        title="Market Potential Bubble Chart"
      />
      <BubbleChart dispatch={dispatch} segments={segments} selectedSegmentId={selectedSegmentId} />
    </article>
  )
}

function ChartHeader({
  subtitle,
  subtitleClassName,
  title,
}: {
  subtitle: string
  /** Overrides the default caption size — e.g. a smaller explanatory note (#3306 #28). */
  subtitleClassName?: string
  title: string
}) {
  return (
    <header className="mb-3">
      <h2 className="text-foreground text-sm font-semibold">{title}</h2>
      <p className={cn("text-muted-foreground text-xs", subtitleClassName)}>{subtitle}</p>
    </header>
  )
}

function EmptyChart() {
  return (
    <div className="bg-muted text-muted-foreground flex aspect-[16/10] items-center justify-center rounded-md text-sm">
      No segments to chart
    </div>
  )
}

function BubbleChart({
  dispatch,
  segments,
  selectedSegmentId,
}: {
  dispatch: Dispatch<SegmentAnalysisAction>
  segments: Segment[]
  selectedSegmentId: string | null
}) {
  if (segments.length === 0) return <EmptyChart />

  // Wide viewBox (~2.5:1) so the chart fills the full-width panel at the ~460px
  // height cap instead of shrinking and leaving a dead band (§G9).
  const width = 800
  const height = 320
  const pad = { top: 18, right: 18, bottom: 40, left: 48 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  // Fit both axes to the actual data range (padded, nice ticks) instead of a
  // fixed clamp — a hard-coded [0, 1300] / [0, 13] left most of the chart
  // blank whenever real data fell well inside those bounds (defect #4).
  // Addressable market cannot be negative, so its axis never pads below 0;
  // CAGR can, so the y-axis stays free.
  const xDomain = fitAxisDomain(segments.map(segmentAddressableTotal), { minZero: true })
  const yDomain = fitAxisDomain(segments.map((segment) => segment.cagr))
  const xScale = (value: number) =>
    pad.left + ((value - xDomain.min) / (xDomain.max - xDomain.min)) * innerW
  const yScale = (value: number) =>
    pad.top + innerH - ((value - yDomain.min) / (yDomain.max - yDomain.min)) * innerH
  const rScale = (value: number) => 5 + Math.sqrt(Math.min(value, 60) / 60) * 18

  return (
    <svg className="h-auto w-full" role="img" viewBox={`0 0 ${width} ${height}`}>
      {xDomain.ticks.map((tick) => (
        <g key={`x-${tick}`} className="fill-muted-foreground">
          <line
            className="stroke-border"
            x1={xScale(tick)}
            x2={xScale(tick)}
            y1={pad.top}
            y2={height - pad.bottom}
          />
          <text fontSize="11" textAnchor="middle" x={xScale(tick)} y={height - 16}>
            {formatAxisTick(tick)}
          </text>
        </g>
      ))}
      {yDomain.ticks.map((tick) => (
        <g key={`y-${tick}`} className="fill-muted-foreground">
          <line
            className="stroke-border"
            x1={pad.left}
            x2={width - pad.right}
            y1={yScale(tick)}
            y2={yScale(tick)}
          />
          <text fontSize="11" textAnchor="end" x={pad.left - 8} y={yScale(tick) + 4}>
            {formatAxisTick(tick)}%
          </text>
        </g>
      ))}
      {segments.map((segment, index) => {
        const color = marketColors[segment.marketName] ?? "#94A3B8"
        const active = segment.id === selectedSegmentId
        const cx = xScale(segmentAddressableTotal(segment))
        const cy = yScale(segment.cagr)
        const r = rScale(segment.sales)
        return (
          <g
            key={segment.id}
            className="segment-chart-node segment-dot-enter cursor-pointer"
            role="button"
            style={{ "--segment-index": index } as CSSProperties}
            tabIndex={0}
            onClick={() => dispatch({ type: "selectSegment", segmentId: segment.id })}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                dispatch({ type: "selectSegment", segmentId: segment.id })
              }
            }}
          >
            <title>{segment.name}</title>
            {active && (
              <circle className="fill-primary" cx={cx} cy={cy} fillOpacity={0.14} r={r + 7} />
            )}
            <circle
              className="segment-chart-dot stroke-card"
              cx={cx}
              cy={cy}
              fill={color}
              opacity={active ? 0.95 : 0.5}
              r={r}
              strokeWidth={2}
            />
          </g>
        )
      })}
    </svg>
  )
}

function ScoreMatrixChart({
  dispatch,
  segments,
  selectedSegmentId,
}: {
  dispatch: Dispatch<SegmentAnalysisAction>
  segments: Segment[]
  selectedSegmentId: string | null
}) {
  if (segments.length === 0) return <EmptyChart />

  const width = 380
  const height = 320
  const pad = { top: 18, right: 16, bottom: 34, left: 38 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  const xScale = (value: number) => pad.left + (value / 10) * innerW
  const yScale = (value: number) => pad.top + innerH - (value / 10) * innerH

  return (
    <svg className="h-auto w-full" role="img" viewBox={`0 0 ${width} ${height}`}>
      {[0, 2, 4, 6, 8, 10].map((tick) => (
        <g key={tick}>
          <line
            className="stroke-border"
            x1={xScale(tick)}
            x2={xScale(tick)}
            y1={pad.top}
            y2={height - pad.bottom}
          />
          <line
            className="stroke-border"
            x1={pad.left}
            x2={width - pad.right}
            y1={yScale(tick)}
            y2={yScale(tick)}
          />
        </g>
      ))}
      {segments.map((segment, index) => {
        const color = marketColors[segment.marketName] ?? "#94A3B8"
        const active = segment.id === selectedSegmentId
        const cx = xScale(segment.fit)
        const cy = yScale(segment.attractiveness)
        return (
          <g
            key={segment.id}
            className="segment-chart-node segment-dot-enter cursor-pointer"
            role="button"
            style={{ "--segment-index": index } as CSSProperties}
            tabIndex={0}
            onClick={() => dispatch({ type: "selectSegment", segmentId: segment.id })}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                dispatch({ type: "selectSegment", segmentId: segment.id })
              }
            }}
          >
            <title>{segment.name}</title>
            {active && (
              <circle className="fill-primary" cx={cx} cy={cy} fillOpacity={0.14} r={12} />
            )}
            <circle
              className="segment-chart-dot stroke-card"
              cx={cx}
              cy={cy}
              fill={color}
              opacity={active ? 0.95 : 0.65}
              r={active ? 7 : 5}
              strokeWidth={1.5}
            />
          </g>
        )
      })}
      <text className="fill-muted-foreground" fontSize="11" x={width - 76} y={height - 10}>
        Strategic fit
      </text>
      <text
        className="fill-muted-foreground"
        fontSize="11"
        transform="rotate(-90 13 110)"
        x="13"
        y="110"
      >
        Attractiveness
      </text>
    </svg>
  )
}

function TreemapChart({
  segments,
  sizeOf = (segment) => segment.sales,
}: {
  segments: Segment[]
  /** Tile value accessor (default = company sales; addressable variant sums addressable). */
  sizeOf?: (segment: Segment) => number
}) {
  const sized = segments.filter((segment) => sizeOf(segment) > 0)
  if (sized.length === 0) return <EmptyChart />

  // Wide viewBox so the tiles fill the full-width panel (a square layout left a
  // large dead band on the right). Wider tiles also let small ones keep a label.
  const width = 820
  const height = 320
  const groups = Object.entries(
    sized.reduce<Record<string, { total: number; count: number }>>((acc, segment) => {
      acc[segment.marketName] ??= { total: 0, count: 0 }
      acc[segment.marketName].total += sizeOf(segment)
      acc[segment.marketName].count += 1
      return acc
    }, {})
  )
    .map<TreemapGroup>(([market, group]) => ({ count: group.count, market, total: group.total }))
    .sort((a, b) => b.total - a.total)
  const rects = layoutTreemap(groups, { height, width, x: 0, y: 0 })

  return (
    <svg className="h-auto w-full" role="img" viewBox={`0 0 ${width} ${height}`}>
      {rects.map((rect, index) => {
        const color = marketColors[rect.market] ?? "#94A3B8"
        const clipId = `segment-tile-${index}`
        const tile = {
          height: Math.max(0, rect.height - 5),
          width: Math.max(0, rect.width - 5),
          x: rect.x + 2.5,
          y: rect.y + 2.5,
        }
        const showLabel = tile.width >= 72 && tile.height >= 42
        const showMetric = tile.width >= 116 && tile.height >= 62
        const maxLabelChars = Math.max(4, Math.floor((tile.width - 18) / 8))
        const segmentLabel = rect.count === 1 ? "segment" : "segments"

        return (
          <g
            key={rect.market}
            className="segment-tile-enter"
            style={{ "--segment-tile-index": index } as CSSProperties}
          >
            <clipPath id={clipId}>
              <rect height={tile.height} rx="8" width={tile.width} x={tile.x} y={tile.y} />
            </clipPath>
            <title>
              {rect.market}: {Math.round(rect.total)} MEUR across {rect.count} {segmentLabel}
            </title>
            <rect
              fill={color}
              height={tile.height}
              rx="8"
              width={tile.width}
              x={tile.x}
              y={tile.y}
            />
            {showLabel && (
              <g clipPath={`url(#${clipId})`}>
                <text
                  fill={readableTextOn(color)}
                  fontSize="12"
                  fontWeight="700"
                  {...svgHaloProps(color)}
                  x={tile.x + 10}
                  y={tile.y + 23}
                >
                  {truncateTileLabel(rect.market, maxLabelChars)}
                </text>
                {showMetric && (
                  <text
                    fill={readableTextOn(color)}
                    fontSize="11"
                    opacity="0.92"
                    {...svgHaloProps(color)}
                    x={tile.x + 10}
                    y={tile.y + 42}
                  >
                    {Math.round(rect.total)} MEUR - {rect.count} {segmentLabel}
                  </text>
                )}
              </g>
            )}
          </g>
        )
      })}
    </svg>
  )
}

function TrendChart({ segments, showTotal }: { segments: Segment[]; showTotal: boolean }) {
  if (segments.length === 0) return <EmptyChart />

  const width = 800
  const height = 320
  const pad = { top: 18, right: 46, bottom: 40, left: 52 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  const series = showTotal ? consolidatedTrend(segments) : (segments[0]?.sparkline ?? [])
  const max = Math.max(...series, 1) * 1.1
  const xAt = (index: number) => pad.left + (index / Math.max(1, series.length - 1)) * innerW
  const yAt = (value: number) => pad.top + innerH - (value / max) * innerH
  const totalPath = series
    .map((point, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yAt(point)}`)
    .join(" ")
  const months = "Jun Jul Aug Sep Oct Nov Dec Jan Feb Mar Apr May".split(" ")

  return (
    <svg className="h-auto w-full" role="img" viewBox={`0 0 ${width} ${height}`}>
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
        <g key={ratio} className="fill-muted-foreground">
          <line
            className="stroke-border"
            x1={pad.left}
            x2={width - pad.right}
            y1={yAt(max * ratio)}
            y2={yAt(max * ratio)}
          />
          <text fontSize="10" textAnchor="end" x={pad.left - 8} y={yAt(max * ratio) + 3}>
            {Math.round(max * ratio)}
          </text>
        </g>
      ))}
      {months.map((month, index) => (
        <text
          key={month}
          className="fill-muted-foreground"
          fontSize="10"
          textAnchor="middle"
          x={xAt(index)}
          y={height - 14}
        >
          {month}
        </text>
      ))}
      {!showTotal &&
        segments.slice(0, 10).map((segment, index) => {
          const color = marketColors[segment.marketName] ?? "#94A3B8"
          const last = segment.sparkline.at(-1) || 1
          const points = segment.sparkline.map((point) => (point / last) * segment.sales)
          const path = points
            .map((point, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yAt(point)}`)
            .join(" ")
          return (
            <path
              key={segment.id}
              className="segment-line-draw"
              d={path}
              fill="none"
              pathLength={1}
              stroke={color}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="0.72"
              strokeWidth="1.8"
              style={{ "--segment-line-index": index } as CSSProperties}
            />
          )
        })}
      {showTotal && (
        <path
          className="segment-line-draw stroke-primary"
          d={totalPath}
          fill="none"
          pathLength={1}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
        />
      )}
    </svg>
  )
}
